import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { SpecraOverlayDrawer, SpecraRecordRail, SpecraTimeline } from '@mono/ui-components/captains';
import { SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { useUserLookup } from '../../../../hooks/useUserLookup';
import { documentErrorText, useDocumentVersions } from './useProposalDocuments';
const T = Testids.proposalDocuments;
const ATTENTION_LABEL = {
    missing: 'Missing',
    unreviewed_amendment: 'Amendment not reviewed',
    changes_to_review: 'Changes to review'
};
/** The real name of a linked document: its stored title, else the file name in its URL. */ export function linkTitle(d) {
    const raw = d.title.trim();
    const looksRaw = raw === '' || /^https?:\/\//i.test(raw) || raw === d.id || raw === d.sourceId;
    if (!looksRaw) return raw;
    const source = d.filename || d.externalUrl;
    try {
        const path = d.filename ? d.filename : new URL(source).pathname;
        const last = decodeURIComponent(path.split('/').filter(Boolean).pop() ?? '');
        if (last) return last;
    } catch  {
    // fall through to the generic name
    }
    return 'Linked document';
}
/** Where a link opens, shown under its title: the site, never the full URL. */ export function linkHost(d) {
    try {
        return new URL(d.externalUrl).hostname.replace(/^www\./, '');
    } catch  {
        return '';
    }
}
export function DocumentsRail({ documents }) {
    const sections = useMemo(()=>{
        const volumes = documents.filter((d)=>d.store === 'volume_document');
        const inPackage = volumes.filter((d)=>d.inPackage).length;
        const pricing = documents.find((d)=>d.store === 'pricing_version');
        const missing = documents.filter((d)=>d.status === 'missing').length;
        const attention = documents.filter((d)=>ATTENTION_LABEL[d.status]);
        return [
            {
                id: 'package',
                title: 'Submission package',
                facts: [
                    {
                        id: 'volumes',
                        label: 'Volumes in the package',
                        value: `${inPackage} of ${volumes.length}`
                    },
                    {
                        id: 'pricing',
                        label: 'Pricing version',
                        value: pricing?.versionLabel || 'None yet'
                    },
                    {
                        id: 'missing',
                        label: 'Missing files',
                        value: `${missing} missing`
                    }
                ]
            },
            {
                id: 'attention',
                title: 'Needs attention',
                rows: attention.map((d)=>({
                        id: d.id,
                        label: d.title,
                        value: /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            children: ATTENTION_LABEL[d.status]
                        })
                    })),
                children: attention.length === 0 ? /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "Nothing needs your attention."
                }) : null
            },
            {
                id: 'visibility',
                title: 'Who can see what',
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "Legal documents such as NDAs and teaming agreements are only visible to the person who filed them and to attorneys. Pricing-only people can upload into Pricing."
                })
            }
        ];
    }, [
        documents
    ]);
    return /*#__PURE__*/ _jsx(SpecraRecordRail, {
        "aria-label": "Document summary",
        "data-testid": T.rail,
        sections: sections,
        getSectionTestId: ()=>T.railSection,
        getRowTestId: (s, _id)=>s.id === 'attention' ? T.railAttention : undefined
    });
}
export function VersionsDrawer(p) {
    const docId = p.doc.shirohigeDocumentId;
    const query = useDocumentVersions(docId);
    const users = useUserLookup(p.projectId);
    const { restoreVersion } = p.writes;
    const current = query.data?.currentVersionId;
    const items = (query.data?.versions ?? []).map((v)=>({
            id: v.id,
            title: `Version ${v.versionNumber}`,
            badge: v.id === current ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "success",
                children: "Current"
            }) : undefined,
            description: [
                v.createdAt ? new Date(v.createdAt).toLocaleString('en-US') : '',
                users.get(v.createdByUserId) || 'Unknown author'
            ].filter(Boolean).join(' · '),
            actions: v.id === current ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                "data-testid": T.versionRestore,
                disabled: !p.doc.canEdit || restoreVersion.isPending,
                title: p.doc.canEdit ? undefined : 'You can view versions but not restore them.',
                onClick: ()=>restoreVersion.mutate({
                        documentId: docId,
                        versionId: v.id,
                        baseVersionId: current
                    }),
                children: "Restore"
            })
        }));
    return /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
        open: true,
        onOpenChange: (open)=>!open && p.onClose(),
        testId: T.versionsDrawer,
        title: "Versions",
        description: p.doc.title,
        children: [
            restoreVersion.isError ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                role: "alert",
                "data-testid": T.versionsError,
                children: [
                    "That version could not be restored. ",
                    documentErrorText(restoreVersion.error)
                ]
            }) : null,
            docId === '' ? /*#__PURE__*/ _jsx("p", {
                "data-testid": T.versionsEmpty,
                children: "This document has no version history."
            }) : query.isPending ? /*#__PURE__*/ _jsx("p", {
                "aria-busy": "true",
                children: "Loading versions…"
            }) : query.isError ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                role: "alert",
                "data-testid": T.versionsError,
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    onClick: ()=>query.refetch(),
                    children: "Try again"
                }),
                children: [
                    "Versions could not be loaded. ",
                    documentErrorText(query.error)
                ]
            }) : items.length === 0 ? /*#__PURE__*/ _jsx("p", {
                "data-testid": T.versionsEmpty,
                children: "No versions have been saved yet."
            }) : /*#__PURE__*/ _jsx(SpecraTimeline, {
                items: items,
                getItemProps: (i)=>({
                        'data-version-id': i.id
                    }),
                getItemTestId: ()=>T.versionRow
            })
        ]
    });
}
